// src/pages/AllVidTutPage.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link as RouterLink } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { tutItems } from '../data/SiteData';

const AllVidTutPage = () => {
  const breadcrumbItems = [
    { title: 'Home', path: '/' },
    { title: 'Resources', path: '/resources' },
    { title: 'Videos & Tutorials', path: '/resources/videos-tutorials' },
  ];

  const [expandedAccordions, setExpandedAccordions] = useState({
    date: false,
    type: false,
  });
  const [showRange, setShowRange] = useState(false);
  const [afterChecked, setAfterChecked] = useState(false);
  const [beforeChecked, setBeforeChecked] = useState(false);
  const [yearAfter, setYearAfter] = useState(new Date().getFullYear());
  const [yearBefore, setYearBefore] = useState(new Date().getFullYear());
  const [monthAfter, setMonthAfter] = useState(new Date().getMonth());
  const [monthBefore, setMonthBefore] = useState(new Date().getMonth());
  const [sortOrder, setSortOrder] = useState('Latest');
  const [showTutorial, setShowTutorial] = useState(true);
  const [showVideo, setShowVideo] = useState(true);

  const handleAccordionToggle = (panel) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [panel]: !prev[panel],
    }));
  };

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  const inputHeight = 32;

  // --- TEXT COLOR CONSTANTS ---
  const enabledTextColor = '#142434';
  const disabledTextColor = '#999C9F';

  const CustomNumberStepper = ({ value, setValue, disabled, min, max }) => {
    const arrowColor = disabled ? disabledTextColor : '#000';
    return (
      <Box
        sx={{
          display: 'flex',
          border: '1px solid #ccc',
          overflow: 'hidden',
          width: '80px',
          height: `${inputHeight}px`,
          backgroundColor: '#fff',
          color: disabled ? disabledTextColor : enabledTextColor,
          fontFamily: 'PT Sans, sans-serif',
          fontSize: '14px',
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {value}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', borderLeft: '1px solid #ccc', width: '24px' }}>
          <IconButton
            size="small"
            onClick={() => !disabled && setValue(Math.min(max, value + 1))}
            sx={{ p: 0, minHeight: 0, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: `8px solid ${arrowColor}`,
              }}
            />
          </IconButton>
          <Divider sx={{ borderColor: '#ccc', m: 0 }} />
          <IconButton
            size="small"
            onClick={() => !disabled && setValue(Math.max(min, value - 1))}
            sx={{ p: 0, minHeight: 0, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: `8px solid ${arrowColor}`,
              }}
            />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const CustomCheckboxIcon = ({ disabled }) => (
    <Box
      sx={{
        width: 18,
        height: 18,
        border: '1px solid #000',
        backgroundColor: disabled ? '#eee' : '#fff',
      }}
    />
  );

  const CustomCheckboxCheckedIcon = ({ disabled }) => (
    <Box
      sx={{
        width: 18,
        height: 18,
        border: '1px solid #000',
        backgroundColor: disabled ? '#ccc' : '#489596',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::after': {
          content: '""',
          width: 6,
          height: 10,
          border: 'solid #fff',
          borderWidth: '0 2px 2px 0',
          transform: 'rotate(45deg)',
        },
      }}
    />
  );

  const CustomRadioIcon = (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: '2px solid #000',
        backgroundColor: '#fff',
        display: 'inline-block',
      }}
    />
  );

  const CustomRadioCheckedIcon = (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: '2px solid #000',
        backgroundColor: '#489596',
        display: 'inline-block',
      }}
    />
  );

  // --- FILTERED & SORTED ITEMS ---
  const filteredItems = useMemo(() => {
    return tutItems
      .filter(item => {
        if (!showTutorial && item.tutType === 'tutorial') return false;
        if (!showVideo && item.tutType === 'video') return false;
        return true;
      })
      .filter(item => {
        if (!showRange) return true;

        const itemDate = new Date(item.date);
        const afterDate = new Date(yearAfter, monthAfter, 1);
        const beforeDate = new Date(yearBefore, monthBefore, 1);

        if (afterChecked && itemDate < afterDate) return false;
        if (beforeChecked && itemDate > beforeDate) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortOrder === 'Latest') return new Date(b.date) - new Date(a.date);
        else return new Date(a.date) - new Date(b.date);
      });
  }, [showRange, afterChecked, beforeChecked, yearAfter, yearBefore, monthAfter, monthBefore, sortOrder, showTutorial, showVideo]);

  return (
    <>
      <Navbar />
      <HeroBanner
        heroTitle="Videos & Tutorials"
        heroDescription="All available tutorials and video content."
        imageUrl="/images/digiTwins.png"
        height="165px"
      />
      <Breadcrumb items={breadcrumbItems} />

      <Box sx={{ padding: '32px', maxWidth: '1300px', margin: '0 auto', display: 'flex', gap: '32px' }}>
        {/* LEFT COLUMN */}
        <Box sx={{ width: '248px', border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden', height: 'fit-content' }}>
          {/* HEADER */}
          <Box sx={{ backgroundColor: 'rgba(72,149,150,0.1)', height: '40px', display: 'flex', alignItems: 'center', px: 2 }}>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '20px', fontWeight: 400, color: enabledTextColor }}>
              Refine Results
            </Typography>
          </Box>

          {/* DATE ACCORDION */}
          <Accordion
            expanded={expandedAccordions.date}
            onChange={() => handleAccordionToggle('date')}
            disableGutters
            square
            sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fff', '& .MuiAccordionSummary-content': { margin: 0 }, px: 2, py: 1.5 }}>
              <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '18px', fontWeight: 400, color: enabledTextColor }}>
                DATE
              </Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ px: 2, backgroundColor: '#F3F3F4', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl component="fieldset">
                <RadioGroup value={showRange ? 'range' : 'all'} onChange={(e) => {
                  const rangeSelected = e.target.value === 'range';
                  setShowRange(rangeSelected);
                  if (!rangeSelected) {
                    setAfterChecked(false);
                    setBeforeChecked(false);
                  }
                }}>
                  <FormControlLabel
                    value="all"
                    control={<Radio icon={CustomRadioIcon} checkedIcon={CustomRadioCheckedIcon} />}
                    label="Show All"
                    sx={{ fontFamily: 'PT Sans, sans-serif', color: enabledTextColor }}
                  />
                  <FormControlLabel
                    value="range"
                    control={<Radio icon={CustomRadioIcon} checkedIcon={CustomRadioCheckedIcon} />}
                    label="Range"
                    sx={{ fontFamily: 'PT Sans, sans-serif', color: enabledTextColor }}
                  />
                </RadioGroup>
              </FormControl>

              {/* RANGE */}
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* AFTER */}
                <Box sx={{ pl: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={afterChecked}
                        onChange={(e) => setAfterChecked(e.target.checked)}
                        disabled={!showRange}
                        icon={<CustomCheckboxIcon disabled={!showRange} />}
                        checkedIcon={<CustomCheckboxCheckedIcon disabled={!showRange} />}
                      />
                    }
                    label="After"
                    sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '15px', color: enabledTextColor }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, pr: 0 }}>
                    <Select
                      size="small"
                      value={monthAfter}
                      onChange={(e) => setMonthAfter(e.target.value)}
                      disabled={!afterChecked}
                      sx={{
                        width: '90px',
                        height: `${inputHeight}px`,
                        backgroundColor: '#fff',
                        color: afterChecked ? enabledTextColor : disabledTextColor,
                        fontFamily: 'PT Sans, sans-serif',
                      }}
                    >
                      {months.map((m, i) => <MenuItem key={m} value={i}>{m}</MenuItem>)}
                    </Select>
                    <CustomNumberStepper
                      value={yearAfter}
                      setValue={setYearAfter}
                      disabled={!afterChecked}
                      min={2000}
                      max={new Date().getFullYear()}
                    />
                  </Box>
                </Box>

                {/* BEFORE */}
                <Box sx={{ pl: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={beforeChecked}
                        onChange={(e) => setBeforeChecked(e.target.checked)}
                        disabled={!showRange}
                        icon={<CustomCheckboxIcon disabled={!showRange} />}
                        checkedIcon={<CustomCheckboxCheckedIcon disabled={!showRange} />}
                      />
                    }
                    label="Before"
                    sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '15px', color: enabledTextColor }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, pr: 0 }}>
                    <Select
                      size="small"
                      value={monthBefore}
                      onChange={(e) => setMonthBefore(e.target.value)}
                      disabled={!beforeChecked}
                      sx={{
                        width: '90px',
                        height: `${inputHeight}px`,
                        backgroundColor: '#fff',
                        color: beforeChecked ? enabledTextColor : disabledTextColor,
                        fontFamily: 'PT Sans, sans-serif',
                      }}
                    >
                      {months.map((m, i) => <MenuItem key={m} value={i}>{m}</MenuItem>)}
                    </Select>
                    <CustomNumberStepper
                      value={yearBefore}
                      setValue={setYearBefore}
                      disabled={!beforeChecked}
                      min={2000}
                      max={new Date().getFullYear()}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* TYPE ACCORDION */}
          <Accordion
            expanded={expandedAccordions.type}
            onChange={() => handleAccordionToggle('type')}
            disableGutters
            square
            sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fff', '& .MuiAccordionSummary-content': { margin: 0 }, px: 2, py: 1.5 }}>
              <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '18px', fontWeight: 400, color: enabledTextColor }}>
                TYPE
              </Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ px: 2, backgroundColor: '#F3F3F4', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showTutorial}
                    onChange={(e) => setShowTutorial(e.target.checked)}
                    icon={<CustomCheckboxIcon disabled={false} />}
                    checkedIcon={<CustomCheckboxCheckedIcon disabled={false} />}
                  />
                }
                label="Tutorial"
                sx={{ fontFamily: 'PT Sans, sans-serif', color: enabledTextColor }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showVideo}
                    onChange={(e) => setShowVideo(e.target.checked)}
                    icon={<CustomCheckboxIcon disabled={false} />}
                    checkedIcon={<CustomCheckboxCheckedIcon disabled={false} />}
                  />
                }
                label="Video"
                sx={{ fontFamily: 'PT Sans, sans-serif', color: enabledTextColor }}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* RIGHT COLUMN */}
        <Box sx={{ flex: 1 }}>
          {/* RESULTS HEADER */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '16px', color: enabledTextColor }}>
              {filteredItems.length} Results
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '16px', color: enabledTextColor }}>
                Sort:
              </Typography>
              <Select
                size="small"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                sx={{ width: '100px', height: '32px', backgroundColor: '#fff', color: enabledTextColor }}
              >
                <MenuItem value="Latest">Latest</MenuItem>
                <MenuItem value="Oldest">Oldest</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* RESULTS LIST */}
          {filteredItems.length === 0 && (
            <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '20px', fontStyle: 'italic', color: disabledTextColor, pl: 5, mb: 2 }}>
              No results have been found. Please edit your filters on the left.
            </Typography>
          )}

          {filteredItems.map((item, index) => {
            const isExternal = item.type === 'external';
            const linkTarget = isExternal ? item.extLink : item.link;

            return (
              <Card
                key={index}
                sx={{ display: 'flex', alignItems: 'flex-start', mb: 3, border: '1px solid #ddd', borderRadius: '8px', boxShadow: 'none', p: 2 }}
              >
                {/* IMAGE */}
                <Box
                  component="a"
                  href={linkTarget}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noopener noreferrer" : undefined}
                  sx={{ display: 'block', mr: 2 }}
                >
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title}
                    sx={{ width: '120px', height: 'auto', borderRadius: '4px', objectFit: 'cover' }}
                  />
                </Box>

                <CardContent sx={{ padding: 0 }}>
                  {/* TITLE */}
                  <Typography
                    component={isExternal ? 'a' : RouterLink}
                    href={isExternal ? linkTarget : undefined}
                    to={!isExternal ? linkTarget : undefined}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '18px',
                      fontWeight: 500,
                      color: '#489596',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      mb: '4px',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {item.title}
                    {isExternal && <LaunchIcon sx={{ fontSize: '18px' }} />}
                  </Typography>

                  <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '14px', color: enabledTextColor, mb: 1 }}>
                    {item.date}
                  </Typography>
                  <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '15px', color: enabledTextColor, lineHeight: 1.4 }}>
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}

          <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '16px', color: enabledTextColor }}>
            {filteredItems.length} Results
          </Typography>
        </Box>
      </Box>

      <Footer />
    </>
  );
};

export default AllVidTutPage;
